import { useEffect, useState } from 'react'
import { preload } from 'react-dom'
import { formatDuration } from '@/site/lib/format'

interface Props {
  youtubeId?: string | null
  image?: string
  title: string
  duration?: string | null
  /** Start (or restart) playback here: { seconds, at } where `at` changes on every request (chapter links). */
  seek?: { seconds: number; at: number } | null
}

/**
 * The tutorial's video: the hero image with a play button until asked, then the YouTube player (so pages
 * don't load YouTube's scripts for every visit).
 */
export default function VideoPlayer({ youtubeId, image, title, duration, seek }: Props) {
  const [playing, setPlaying] = useState(false)
  useEffect(() => {
    if (seek) setPlaying(true)
  }, [seek])
  // The hero is the page's largest paint: fetch it first.
  if (image) preload(image, { as: 'image', fetchPriority: 'high' })
  const length = formatDuration(duration)

  return (
    <div className="relative aspect-video overflow-hidden rounded-[22px] bg-qt-text md:rounded-3xl">
      {playing && youtubeId ? (
        <iframe
          key={seek?.at ?? 0}
          src={`https://www.youtube.com/embed/${encodeURIComponent(youtubeId)}?rel=0&autoplay=1${seek ? `&start=${Math.floor(seek.seconds)}` : ''}`}
          title={`${title} (video)`}
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 size-full"
        />
      ) : (
        <>
          {image && <img src={image} alt={title} width="1280" height="720" className="absolute inset-0 size-full object-cover" />}
          {youtubeId && (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              className="absolute right-4 bottom-4 flex h-12 items-center gap-2 rounded-2xl bg-qt-badge px-5 text-base font-extrabold text-qt-badge-ink shadow-md hover:brightness-95 md:right-6 md:bottom-6 md:h-14"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
              </svg>
              Watch{length && <span className="font-bold">· {length}</span>}
            </button>
          )}
        </>
      )}
    </div>
  )
}
